/**
 * Shared listing helpers — display formatting + adjacent occasion
 * lookup. The canonical occasion catalog (labels, emojis,
 * subcategories, legacy aliases, adjacency) lives in
 * src/data/occasions.js — this module is the public API the rest
 * of the codebase consumes.
 *
 * Restructured 2026-05-15: previously this module held its own
 * EVENT_TYPE_LABELS / CATEGORY_EMOJIS / ADJACENT_OCCASIONS tables
 * that drifted out of sync with the wizard form, homepage tiles,
 * and SubThemeFilters' data file. occasions.js consolidated them;
 * the constants below are re-exports for backward compatibility.
 */

import {
  OCCASIONS,
  ADJACENT_OCCASIONS as OCCASIONS_ADJACENCY,
  findOccasion,
  resolveOccasion,
  occasionLabel,
} from '../data/occasions';
import { types as sdkTypes } from './sdkLoader';

const { Money } = sdkTypes;

// ─── Event Type Labels ───

// Build the label map from the catalog + legacy aliases for back-
// compat. Listings tagged with `cultural` resolve to "Holiday";
// `kids_birthday` resolves to "Birthday"; etc. Once the backfill
// script runs verify-clean, the legacy entries can be pruned.
export const EVENT_TYPE_LABELS = {
  ...OCCASIONS.reduce((acc, o) => ({ ...acc, [o.key]: o.label }), {}),
  // Legacy aliases. resolveOccasion(legacy) → new key → catalog label.
  cultural: occasionLabel('cultural'),
  beach_day: occasionLabel('beach_day'),
  themed_dinners: occasionLabel('themed_dinners'),
  game_day: occasionLabel('game_day'),
  kids_birthday: 'Birthday',
};

export const formatEventType = eventType =>
  EVENT_TYPE_LABELS[eventType] || eventType || '';

// ─── Category Emoji Map ───

// Same pattern: derive from catalog + legacy aliases. Catalog
// emojis are the source of truth.
export const CATEGORY_EMOJIS = {
  ...OCCASIONS.reduce((acc, o) => ({ ...acc, [o.key]: o.emoji }), {}),
  // Legacy aliases resolve through findOccasion to the new entry.
  cultural: findOccasion('cultural')?.emoji,
  beach_day: findOccasion('beach_day')?.emoji,
  themed_dinners: findOccasion('themed_dinners')?.emoji,
  game_day: findOccasion('game_day')?.emoji,
  kids_birthday: '🎂',
};

export const getCategoryEmoji = category =>
  CATEGORY_EMOJIS[category] || '📦';

// ─── Adjacent occasions ─── (Stream 3 PR-5)
//
// Re-exported from occasions.js for back-compat. The map only
// includes the canonical 8 keys; legacy aliases (cultural / beach_day
// / etc.) resolve through getAdjacentOccasions() below.
export const ADJACENT_OCCASIONS = OCCASIONS_ADJACENCY;

/**
 * Return up to `limit` adjacent occasion keys for the given
 * occasion. Resolves legacy aliases first so a listing tagged
 * `cultural` returns Holiday's neighbours. Empty array for
 * unknown / missing keys.
 */
export const getAdjacentOccasions = (occasionKey, limit = 3) => {
  if (!occasionKey || typeof occasionKey !== 'string') return [];
  const resolved = resolveOccasion(occasionKey);
  const list = ADJACENT_OCCASIONS[resolved];
  return Array.isArray(list) ? list.slice(0, limit) : [];
};

// ─── City extraction ─── (Stream 3 PR-5)
//
// Pull a renter-friendly city string out of the search page's
// `address` query param. The geolocation autocomplete returns
// long-form strings like "Goleta, CA 93117, USA" or
// "Santa Barbara, California, United States" — we want the first
// segment ("Goleta" / "Santa Barbara") for the empty-state
// headline. Falls back to the raw input if no comma is present.
export const cityFromSearchAddress = address => {
  if (!address || typeof address !== 'string') return '';
  const first = address.split(',')[0]?.trim();
  return first || '';
};

// ─── Price with Fees ───

// Customer commission rate (matches Sharetribe Console setting)
export const CUSTOMER_FEE_RATE = 0.15;

/**
 * Calculate all-in price including customer fees.
 * @param {number} amountInSubunits — price in cents
 * @returns {number} — price with fees in cents
 */
export const priceWithFees = amountInSubunits => {
  if (!amountInSubunits || isNaN(amountInSubunits)) return 0;
  return Math.round(amountInSubunits * (1 + CUSTOMER_FEE_RATE));
};

/**
 * Format price with fees as a display string.
 * @param {number} amountInSubunits — price in cents
 * @returns {string} — formatted price like "$92"
 */
export const formatPriceWithFees = amountInSubunits => {
  const total = priceWithFees(amountInSubunits);
  return `$${(total / 100).toFixed(0)}`;
};

/**
 * Convert a base-price Money into the all-in Money the renter actually
 * pays (base + 15% customer fee). This is the ONE place to turn a raw
 * listing `price` into its renter-facing amount for use with
 * formatMoney(intl, ...). Every listing-price shown to a renter must go
 * through the fee (priceWithFees / this helper / the inline ×1.15 in
 * ListingCard.helpers, OrderPanel, TierSelector, MobileBookingBar) —
 * showing the bare base price leaks our markup (a renter could subtract
 * it from the checkout total to compute our fee). Returns null for a
 * missing/invalid price so callers keep their existing "no price" branch.
 *
 * @param {Money} price — base listing price (Sharetribe Money, cents)
 * @returns {Money|null} — all-in Money, or null when price is unusable
 */
export const allInMoney = price => {
  if (!price || typeof price.amount !== 'number' || !price.currency) return null;
  return new Money(priceWithFees(price.amount), price.currency);
};

// ─── Address Formatting ───

/**
 * Extract "City, State" from a full address string.
 * e.g. "123 Main St, Santa Barbara, CA 93101, USA" → "Santa Barbara, CA"
 */
// DEPRECATED 2026-07-07: written for pre-privacy street-first addresses;
// against post-#336 public addresses it rendered "California 93117,
// United States". Use cityStateLabel from util/search instead.
export const formatCityState = address => {
  if (!address) return '';
  const parts = address.split(',').map(p => p.trim());
  if (parts.length >= 3) {
    const city = parts[1];
    const stateZip = parts[2];
    const state = stateZip.replace(/\d{5}(-\d{4})?/, '').trim();
    return `${city}, ${state}`;
  }
  return address;
};
